/**
 * type-selector — the event page's ticket-type list. The
 * first-currently-purchasable type is auto-selected at qty 1 by
 * checkout.sync.ts's page-land write (event-page.state.ts); this view is
 * pure render + gesture, no auto-select logic of its own. Single- and
 * multi-type events share one row template so the collapsed page never
 * reads as two different products depending on how many types an event
 * sells.
 *
 * The page mounts this tag TWICE where an event has programme days:
 * `section="wholeEvent"` above the chip row, carrying the types tied to no
 * single day under the one heading that names what they are, and the default
 * mount below it, carrying the selected day's own types. The same tag rather
 * than a second component because both render the identical ticket row, and a
 * row built twice is a second ticket type from the buyer's side. The section
 * mount renders nothing at all — no heading, no box — when its set is empty,
 * so an event with no programme days is the one flat list it has always been.
 *
 * Every row holds one fixed geometry across every state: the left column is
 * exactly two lines — the type's name with the door beside it, then the price
 * with the volume-discount and scarcity/held-status hints as colored spans
 * next to it — and the right slot (fixed width, stepper height) renders EITHER
 * the stepper OR the row's status ("Ausverkauft", "ab 15.08.", "Verkauf
 * beendet") in its place. No advisory line ever appends; nothing expands or
 * collapses. The stepper's focal pair sits on the golden ratio: qty numeral
 * headline/lg (32px) against chrome-less size-sm glyphs (20px) — 32/20 = 1.6 ≈
 * φ; the count is the information, the buttons the quiet affordance, tap
 * targets stay 40px. The order-cap advisory occupies a constant-height line
 * below the list.
 *
 * A type's own optional description costs the list nothing, because the row
 * never prints one: the description lives behind the door, and the door's own
 * box is reserved on every row whether or not there is anything to open. So a
 * described row and a bare one measure identically down the whole list —
 * 70px each at a 358px phone measure and unchanged at 390px and 640px — and
 * the only thing an operator's sentence changes about this page is whether one
 * icon is live.
 *
 * That door is two controls onto one overlay, on every row carrying a
 * description and at every width: the type's NAME, whose own mark rests
 * transparent and returns on hover and focus, and an `info` icon-button beside
 * it that carries the door at rest. It opens the type read whole in a
 * `ticket-type-description` overlay — both facts at reading scale, the full
 * text untruncated at any length since nothing bounds it upstream, and a
 * stepper that keeps buying reachable while the buyer reads. Touch-safe where
 * a hover tooltip is not, and keyed on whether a description exists rather
 * than on whether a line happened to overflow, so a described type offers the
 * same door on a phone and on a laptop. The description stays offered in every
 * row state — sold out, outside its sales window, or basketLocked-readonly —
 * since it describes the type itself, not its current purchasability. See
 * ticket-type-row.view.ts for how the two doors are built and why they carry
 * different accessible names.
 *
 * Once the buyer commits to pay (checkout.state.ts's basketLocked — the
 * journey past "shopping"), the SAME rows go readonly in place — every row
 * the shopping list showed still renders, at the same height and in the same
 * order, and only the right-hand slot changes. A line they bought keeps its
 * qty numeral at its usual weight, standing at the same x the stepper's
 * numeral stood at; a line they left at zero gives its slot up entirely,
 * since a receded `0` is more ink than the `1` beside it and would outweigh
 * the one line that matters; a line that could never be bought goes on
 * saying so — "Ausverkauft", "ab 15.08." — because that is a fact about the
 * type, not a quantity anyone chose. Absence of chrome is the whole "you
 * can't edit this right now" signal, no lock glyph, and everything but the
 * bought lines recedes so the summary reads as one. Holding the list's exact
 * height across that swap is the point: the frost scrim above it exists to
 * stop the page moving, and on a card submit the mounted Revolut iframe the
 * buyer is typing into rides whatever this list does. The advisory slot
 * swaps in a "locked while we confirm" line with an inline edit-basket
 * affordance that reopens the journey to "shopping" — the live Revolut order
 * is never touched, it re-prices on re-edit via the existing debounced
 * write.
 *
 * Shared production view — the designer studio mounts this same tag
 * against fixtures; web mounts it against the store. The row itself is
 * ticket-type-row.view.ts, a checkout-agnostic presentational component;
 * this file resolves every checkout-specific field onto it (discount/
 * scarcity/sale-window hints, the basketLocked readonly slot) and owns
 * the qty write. Keyboard activation (role="button" + Enter/Space) is
 * native to the row component itself, not wired up by this file.
 */

import { formatCurrency } from "@mhmo91/rx";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
import { html, nothing } from "lit";
import { property } from "lit/decorators.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";
import { salesPaused } from "../licence/licence.state.ts";
import { basketLocked, checkoutJourney } from "./checkout.state.ts";
import { eventDetailState } from "./event-header/event-header.state.ts";
import {
	atOrderCap,
	layoutMode,
	type ShopTypeRow,
	selectedDayRows,
	setQty,
	wholeEventRows,
} from "./event-page/event-page.state.ts";
import { playSoundCue } from "./sound-cues.ts";
import type {
	TicketTypeRow,
	TicketTypeRowHint,
} from "./ticket-type-row.view.ts";
import "./ticket-type-row.view.ts";

@schmancyElement("type-selector")
export class TypeSelectorView extends LocalizeMixin(SchmancyElement) {
	/** Which of the page's two mounts this one is. A property and not a state
	 *  signal because it names the mount, not something the page and this
	 *  component share: two mounts of one tag on one page need two different
	 *  answers, which a module-scope signal cannot give. */
	@property({ type: String, reflect: true }) section: "wholeEvent" | null =
		null;

	override render() {
		// The section above the chips. It carries the rows and their one
		// heading and nothing else: the sold-out messages, the whole list's
		// skeleton and the cap advisory all belong to the day's own list below,
		// where the buyer is standing. Empty means absent — a heading over no
		// rows announces a section that is not there.
		if (this.section === "wholeEvent") {
			const rows = wholeEventRows.get();
			if (rows.length === 0) return nothing;
			return html`
				<div class="flex flex-col gap-2">
					<schmancy-typography type="label" token="sm" transform="uppercase" class="tracking-widest text-surface-onVariant">
						${this.t("storefront.typeSelector.wholeEventTitle")}
					</schmancy-typography>
					${repeat(
						rows,
						(r) => r.id,
						(r) => this.ticketRow(r, basketLocked.get()),
					)}
				</div>
			`;
		}

		const mode = layoutMode.get();
		const locked = basketLocked.get();

		if (mode === "loading") {
			return html`
				<div class="flex flex-col gap-2" aria-hidden="true">
					<schmancy-skeleton class="h-16 w-full rounded-xl"></schmancy-skeleton>
					<schmancy-skeleton class="h-16 w-full rounded-xl"></schmancy-skeleton>
				</div>
			`;
		}

		// While sales are paused the pay area's notice stands in place of every
		// sold-out message: a buyer never reads "sold out" and "paused" together.
		const paused = salesPaused.value;
		const eventWide = mode === "soldOut" || mode === "notOnSale";
		if (eventWide && !locked && paused) return nothing;

		// Two event-wide reasons, one stage: a catalog with nothing offered to
		// the public never sold out, so it says what is true instead.
		if (eventWide && !locked) {
			return html`
				<div class="flex flex-col items-start gap-2 py-4">
					<schmancy-typography type="title" token="sm">${this.t(mode === "notOnSale" ? "storefront.typeSelector.notOnSaleTitle" : "storefront.typeSelector.soldOutTitle")}</schmancy-typography>
					<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
						${this.t(mode === "notOnSale" ? "storefront.typeSelector.notOnSaleBody" : "storefront.typeSelector.soldOutBody")}
					</schmancy-typography>
				</div>
			`;
		}

		// One row template for every list: single- and multi-type events render
		// identically, and so do the selected day's types and the section
		// above the chips — every revealed row shows, notYetOpen/closed
		// included.
		const confirming = checkoutJourney.value === "confirming";
		const cap = eventDetailState.value.event?.maxTicketsPerOrder;
		const [lockedPrefix, lockedSuffix] = this.t(
			"storefront.typeSelector.basketLocked",
		).split("{{editBasket}}");

		return html`
			<div class="flex flex-col gap-2">
				${when(
					mode === "daySoldOut" && !locked,
					// The DAY is gone, not the event: the chips above still lead
					// somewhere, and the section above them and the payment section
					// beneath stay exactly where the buyer left them.
					() =>
						paused
							? html``
							: html`
						<div class="flex flex-col items-start gap-2 py-4">
							<schmancy-typography type="title" token="sm">${this.t("storefront.typeSelector.daySoldOutTitle")}</schmancy-typography>
							<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
								${this.t("storefront.typeSelector.daySoldOutBody")}
							</schmancy-typography>
						</div>
					`,
					() =>
						html`<div class="flex flex-col gap-2">${repeat(
							selectedDayRows.get(),
							(r) => r.id,
							(r) => this.ticketRow(r, locked),
						)}</div>`,
				)}
				<!-- Two lines' worth of box, held at every width and in every state.
				     The locked advisory is one line on a laptop and two on a phone,
				     and it arrives at the pay tap — the exact instant the page is
				     under a scrim whose job is to hold everything still, with a card
				     iframe below it. Reserving the taller measure unconditionally
				     also survives translation: the width a given locale's string
				     wraps at is not something a breakpoint here can know. -->
				<div class="flex min-h-8 items-center">
					${
						confirming
							? html`
								<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
									${lockedPrefix}<a
										href="#"
										class="font-semibold text-primary-default hover:underline"
										@click=${(e: Event) => {
											e.preventDefault();
											checkoutJourney.replace("shopping");
											playSoundCue("cancel");
										}}
									>${this.t("storefront.typeSelector.editBasket")}</a>${lockedSuffix}
								</schmancy-typography>
							`
							: when(
									atOrderCap.get() && cap != null,
									() => html`
										<schmancy-typography type="body" token="sm" class="text-surface-onVariant">
											${cap === 1 ? this.t("storefront.typeSelector.orderCapOne") : this.t("storefront.typeSelector.orderCap", { cap: cap ?? 0 })}
										</schmancy-typography>
									`,
								)
					}
				</div>
			</div>
		`;
	}

	/** One row, resolved identically wherever it sits — the section above the
	 *  chips and the selected day's list beneath them render the same ticket,
	 *  and a row that read differently in one of them would be a second ticket
	 *  type from the buyer's side. */
	private ticketRow(r: ShopTypeRow, locked: boolean) {
		const discountTier = r.volumeDiscounts[0];
		const selected = r.qty > 0;
		// A row that cannot be bought says why, locked or not: "cannot be
		// bought" is a fact about the type, and the qty numeral has no
		// way to carry it — a sold-out row rendered as `0` reads as a
		// quantity the buyer chose rather than a thing that was gone.
		const paused = salesPaused.value;
		const showStatus =
			!r.purchasable && !selected && !(paused && r.bucket === "soldOut");
		const hints: TicketTypeRowHint[] = [];
		if (discountTier) {
			hints.push({
				text: this.t("storefront.typeSelector.volumeDiscount", {
					minQty: discountTier.minQty,
					price: formatCurrency(
						discountTier.unitPrice / 100,
						"EUR",
						this.locale,
					),
				}),
			});
		}
		if (r.bucket === "low" && r.purchasable) {
			hints.push({
				text: this.t("storefront.typeSelector.lowStock"),
				tone: "warning",
			});
		}
		if (r.bucket === "soldOut" && selected && !locked && !paused) {
			hints.push({
				text: this.t("storefront.typeSelector.soldOutAfterSelection"),
				tone: "error",
			});
		}
		const row: TicketTypeRow = {
			id: r.id,
			name: r.name,
			description: r.description ?? null,
			priceLabel:
				r.price === 0
					? this.t("storefront.typeSelector.free")
					: formatCurrency(r.price / 100, "EUR", this.locale),
			hints,
			selected,
			// A row nobody bought recedes once the basket is locked, for
			// the same reason a sold-out one does: it is on the page to
			// hold its place, not to be read as part of the purchase.
			// Paused sales dim every row: none of them can be bought.
			receded: paused || (!selected && (locked || r.bucket === "soldOut")),
			clickable: !locked && !paused && r.canIncrement,
			rowAriaLabel: null,
			nameButtonAriaLabel: this.t(
				"storefront.typeSelector.nameButtonAriaLabel",
				{ name: r.name },
			),
			descriptionButtonAriaLabel: this.t(
				"storefront.typeSelector.descriptionButtonAriaLabel",
				{ name: r.name },
			),
			descriptionBackLabel: this.t(
				"storefront.typeSelector.descriptionBackLabel",
			),
			slot: showStatus
				? {
						kind: "status",
						tone: r.bucket === "soldOut" ? "error" : "muted",
						label:
							r.bucket === "soldOut"
								? this.t("storefront.typeSelector.soldOutStatus")
								: r.salesWindowStatus === "notYetOpen" && r.opensAt
									? this.t("storefront.typeSelector.opensOn", {
											date: r.opensAt
												.toDate()
												.toLocaleDateString(
													this.uiLanguage === "de" ? "de-DE" : "en-GB",
													{
														day: "2-digit",
														month: "2-digit",
														timeZone: r.timezone,
													},
												),
										})
									: this.t("storefront.typeSelector.saleEnded"),
					}
				: locked
					? selected
						? { kind: "lockedQty", qty: r.qty }
						: // Nothing bought on a line the buyer could still have
							// bought: the slot holds its box and prints nothing,
							// because a `0` here is ink the committed lines have to
							// compete with rather than a fact anyone needs.
							{ kind: "reserved" }
					: {
							kind: "stepper",
							qty: r.qty,
							canIncrement: !paused && r.canIncrement,
							canDecrement: !paused && r.canDecrement,
							incrementAriaLabel: this.t(
								"storefront.typeSelector.incrementAriaLabel",
								{ name: r.name },
							),
							decrementAriaLabel: this.t(
								"storefront.typeSelector.decrementAriaLabel",
								{ name: r.name },
							),
							// A buyer's qty lives in single digits under the order
							// cap — nothing a keyboard reaches faster than +/−.
							qtyField: null,
						},
		};
		return html`
			<ticket-type-row
				.row=${row}
				@increment=${() => {
					setQty(r.id, r.qty + 1);
					playSoundCue("add-to-cart");
				}}
				@decrement=${() => {
					setQty(r.id, r.qty - 1);
					playSoundCue("remove-from-cart");
				}}
			></ticket-type-row>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"type-selector": TypeSelectorView;
	}
}
